import { useState } from 'react';
import { useQuery } from '@apollo/client';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Avatar from '@mui/material/Avatar';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Paper from '@mui/material/Paper';
import Rating from '@material-ui/lab/Rating';
import { GET_COMENTARIOS_PRESTACION } from '../../../Querys/prestaciones/querys';
import '../css/ModalPrestacion.css';

interface Props {
  open: boolean;
  onClose: () => void;
  idPrestacion: number | string;
  tituloPrestacion: string;
}

const toTitleCase = (str: string) =>
  str
    .toLowerCase()
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const ROWS_PER_PAGE = 5;

const ComentariosPrestacionModal = ({ open, onClose, idPrestacion, tituloPrestacion }: Props) => {
  const [page, setPage] = useState(0);
  const { data, loading } = useQuery(GET_COMENTARIOS_PRESTACION, {
    variables: { idPrestacion: String(idPrestacion) },
    skip: !open,
  });

  const comentarios: any[] = data?.GET_COMENTARIOS_PRESTACION ?? [];
  const paginados = comentarios.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE);

  const formatFecha = (dateStr: string) => {
    if (!dateStr) return '';
    let d: Date;
    if (/^\d+$/.test(dateStr.trim())) {
      d = new Date(Number(dateStr));
    } else {
      d = new Date(dateStr);
      if (isNaN(d.getTime())) d = new Date(dateStr.trim().replace(/\s/, 'T'));
    }
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getInitials = (nombre: string, apellido: string) =>
    `${nombre?.[0] ?? ''}${apellido?.[0] ?? ''}`.toUpperCase();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{ position: 'absolute', right: 8, top: 8, color: (t) => t.palette.grey[500] }}
      >
        <CloseIcon />
      </IconButton>

      <DialogTitle>
        <span className="titlePrestacion">
          Comentarios — {toTitleCase(tituloPrestacion)}
        </span>
      </DialogTitle>

      <DialogContent dividers sx={{ padding: 0 }}>
        {loading ? (
          <p style={{ textAlign: 'center', color: '#555', padding: '24px 0' }}>Cargando...</p>
        ) : comentarios.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#888', padding: '24px 0' }}>
            Esta prestación aún no tiene comentarios.
          </p>
        ) : (
          <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell className='pd_contenedorTitulos'>Colaborador</TableCell>
                  <TableCell className='pd_contenedorTitulos' style={{ whiteSpace: 'nowrap' }}>Fecha</TableCell>
                  <TableCell className='pd_contenedorTitulos'>Calificación</TableCell>
                  <TableCell className='pd_contenedorTitulos'>Comentario</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginados.map((c: any) => (
                  <TableRow key={c.id} hover sx={{ verticalAlign: 'top' }}>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Avatar
                          src={c.avatar || undefined}
                          alt={c.nombreColaborador}
                          sx={{ width: 32, height: 32, fontSize: 13 }}
                        >
                          {!c.avatar && getInitials(c.nombreColaborador, c.apellido)}
                        </Avatar>
                        <span style={{ fontSize: 12, fontFamily: 'Poppins, sans-serif', color: '#093C5D', fontWeight: 600 }}>
                          {c.nombreColaborador} {c.apellido}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>
                      <span style={{ fontSize: 12, color: '#555' }}>
                        {formatFecha(c.dateAdd)}
                      </span>
                    </TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>
                      {c.calificacion != null ? (
                        <Rating
                          name={`rating-${c.id}`}
                          value={c.calificacion}
                          precision={0.5}
                          size="small"
                          readOnly
                        />
                      ) : (
                        <span style={{ fontSize: 11, color: '#aaa' }}>—</span>
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 200, maxWidth: 380 }}>
                      <span style={{ fontSize: 12, color: '#333', fontFamily: 'Roboto, sans-serif', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                        {c.comentario}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={comentarios.length}
              page={page}
              rowsPerPage={ROWS_PER_PAGE}
              rowsPerPageOptions={[]}
              onPageChange={(_, newPage) => setPage(newPage)}
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
            />
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ComentariosPrestacionModal;
